import React, {
  ChangeEvent,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
  memo,
} from "react";
import {
  TextValidator,
  ValidatorComponentProps,
} from "react-material-ui-form-validator";
import { Await } from "react-router";
import FormFieldSkeleton from "components/skeletons/FormField";
import { SxProps } from "@mui/material";
import { capitalizeFirstLetter } from "utils/stringFormatters";

export interface AuturaTextFieldProps {
  id?: string;
  name: string;
  value: string | number;
  type?: "text" | "password" | "email" | "number" | "tel" | "url";
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps | undefined;
  inputSx?: SxProps | undefined;
  readonly?: boolean;
  required?: boolean;
  variant?: "standard" | "outlined" | "filled";
  fullWidth?: boolean;
  margin?: "none" | "dense" | "normal";
  tabIndex?: number;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  validators?: ValidatorComponentProps["validators"];
  errorMessages?: ValidatorComponentProps["errorMessages"];
  InputSx?: SxProps | undefined;
  helperText?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  initialValue?: string | undefined;
  disableUnderline?: boolean;
  resolve?: Promise<any>;
  textAlign?: "left" | "right" | "center";
  className?: string;
  autoFocus?: boolean;
  notched?: boolean;
  shrink?: boolean;
  maxLength?: number;
  multiline?: boolean;
  maxRows?: number;
  minRows?: number;
  rows?: number;
  autoComplete?: string;
}

const AuturaTextField = ({
  id,
  value,
  onChange,
  name,
  type = "text",
  variant = "outlined",
  margin = "none",
  validators = [],
  errorMessages = [],
  helperText,
  label,
  startAdornment,
  endAdornment,
  readonly = false,
  required = false,
  fullWidth = true,
  disableUnderline = false,
  placeholder,
  sx,
  inputSx,
  InputSx,
  disabled = false,
  initialValue,
  resolve,
  textAlign = "left",
  className,
  autoFocus = false,
  notched,
  shrink,
  tabIndex,
  maxLength,
  multiline = false,
  maxRows,
  minRows,
  rows,
  autoComplete,
}: AuturaTextFieldProps): ReactNode => {
  let calcLabel = label;

  if (required) {
    validators = ["required", ...validators];
    errorMessages = [`${calcLabel} is required`, ...errorMessages];
    if (calcLabel) {
      calcLabel = `${calcLabel} *`;
    }
  }

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  
  return (
    <Suspense
      fallback={
        <FormFieldSkeleton
          id={id}
          variant={variant}
          margin={margin}
          initialValue={initialValue}
          required={required}
        />
      }
    >
      <Await resolve={resolve}>
        <TextValidator
          inputRef={inputRef}
          className={className}
          sx={{ ...sx }}
          id={id || name}
          name={name}
          aria-label={name}
          value={value || ""}
          onChange={onChange}
          type={type}
          label={calcLabel}
          fullWidth={fullWidth}
          autoFocus={autoFocus}
          disabled={disabled}
          variant={variant}
          tabIndex={tabIndex}
          validators={validators}
          errorMessages={errorMessages}
          placeholder={placeholder}
          helperText={capitalizeFirstLetter(helperText)}
          margin={margin}
          multiline={multiline}
          maxRows={maxRows}
          minRows={minRows}
          rows={rows}
          slotProps={{
            htmlInput: {
              maxLength: maxLength,
              autoComplete: autoComplete,
              style: {
                cursor: readonly ? "default" : "text",
                textAlign: textAlign,
                ...inputSx,
              },
            },
            input: {
              notched: notched,
              readOnly: readonly,
              startAdornment: startAdornment,
              endAdornment: endAdornment,
              disableUnderline: disableUnderline,
              sx: InputSx,
            },
            inputLabel: {
              shrink: shrink,
            },
          }}
        />
      </Await>
    </Suspense>
  );
};

export default memo(AuturaTextField);
